import { debounce } from 'lodash'
import { Module } from 'vuex'
import { isPageFilenameValid } from '@/helpers/isPageFilenameValid'
import { postError, postSuccess, postWarning } from '@/helpers/notification'
import { PdfImportController } from '@/controllers/admin/pdfImport'
import {
  BulkResetPagesResponse,
  MatchingPageResult,
  PageDetailsInfoRequest,
  PageDetailsInfoResponse,
  PageNavigation,
  PdfImportPageTile,
  ReviewStatus
} from '@/interfaces/admin/pdfImport'
import i18n from '@/locales'
import router from '@/router'

/**
 * PDF Import Page Details Store
 *
 * This store serves a dual purpose:
 *
 * 1. PARENT COORDINATOR ROLE:
 *    - Manages page-level context (importId, pageId, navigation)
 *    - Aggregates dirty state from child stores (BOM, Illustrations)
 *    - Provides common page metadata (page number, origin, counts)
 *    - Coordinates initialization for all page detail views
 *
 * 2. INFO VIEW DATA ROLE:
 *    - Manages Info tab-specific data (page filename, translations, review status)
 *    - Handles Info view save/revert operations
 *    - Maintains matching page search functionality
 *
 * CHILD STORES:
 *    - pdfImportPageDetailsBom: Manages BOM view data and operations
 *    - pdfImportPageDetailsIllustrations: (Future) Manages Illustrations view data
 *
 * ARCHITECTURE NOTES:
 *    - Child stores update parent counts via root commits (e.g., UPDATE_BOM_ITEM_COUNT)
 *    - Parent aggregates isDirty/canSave from all child stores
 *    - Save/revert actions are routed by route name in PdfImportPageDetails.vue
 */
export interface PdfImportPageDetailsState {
  importId: number | null
  pageId: number | null
  isDirty: boolean
  loading: boolean
  locked: boolean
  matchingPageLookupVersion: number
  saving: boolean
  searchingMatchingPage: boolean
  originalPageFilename: string | null
  originalPageInfo: PageDetailsInfoResponse | null
  pageInfo: PageDetailsInfoResponse | null
}

const initialState: PdfImportPageDetailsState = {
  importId: null,
  pageId: null,
  isDirty: false,
  loading: false,
  locked: false,
  matchingPageLookupVersion: 0,
  saving: false,
  searchingMatchingPage: false,
  originalPageFilename: null,
  originalPageInfo: null,
  pageInfo: null
}

// Tracks how many matching-page lookups are currently in flight. Overlapping lookups
// (a new one starting before an older one's network call resolves) must not let an
// older lookup's completion clear searchingMatchingPage while a newer one is still
// running, so the flag is only cleared once no lookup remains active.
let activeSearchCount = 0

const debouncedSearchMatchingPage = debounce(async function({ state, commit }: any) {
  if (!state.pageInfo) return

  const requestVersion = state.matchingPageLookupVersion
  const { pageFilename, translation } = state.pageInfo
  const locale = translation.locale

  // Skip search if filename or translation name are missing
  if (!pageFilename?.trim() || !translation.name?.trim()) {
    return
  }

  try {
    activeSearchCount++
    commit('SET_SEARCHING_MATCHING_PAGE', true)
    const result = await PdfImportController.searchMatchingPage({
      pageFilename: pageFilename?.trim() || null,
      translationName: translation.name?.trim() || null,
      locale
    })
    // The lookup inputs changed (edited filename/name) or a fresher page-info load
    // (e.g. from resetPage) landed while this was in flight — applying this result
    // now would overwrite it with a stale matchingPage value.
    if (state.matchingPageLookupVersion === requestVersion) {
      commit('UPDATE_MATCHING_PAGE', result)
    }
  } catch (error: any) {
    throw error
  } finally {
    activeSearchCount--
    if (activeSearchCount === 0) {
      commit('SET_SEARCHING_MATCHING_PAGE', false)
    }
  }
}, 500)

const pdfImportPageDetails: Module<PdfImportPageDetailsState, any> = {
  namespaced: true,

  state: { ...initialState },

  getters: {
    bomItemCount: (state) => state.pageInfo?.bomItemCount ?? 0,
    canSave: (state, getters, rootState, rootGetters) => {
      const bomIsDirty = rootGetters['pdfImportPageDetailsBom/bomIsDirty'] || false
      const bomCanSave = rootGetters['pdfImportPageDetailsBom/bomCanSave'] || false
      const illustrationsIsDirty =
        rootGetters['pdfImportPageDetailsIllustrations/illustrationsIsDirty'] || false
      const illustrationsCanSave =
        rootGetters['pdfImportPageDetailsIllustrations/illustrationsCanSave'] || false

      if (bomIsDirty) {
        return bomCanSave
      }

      if (illustrationsIsDirty) {
        return illustrationsCanSave
      }

      if (!state.isDirty || !state.pageInfo) return false
      return isPageFilenameValid(state.pageInfo.pageFilename) && !!state.pageInfo.translation.name?.trim()
    },
    illustrationCount: (state) => state.pageInfo?.illustrationCount ?? 0,
    importId: (state) => state.importId,
    isDirty: (state, getters, rootState, rootGetters) => {
      const bomIsDirty = rootGetters['pdfImportPageDetailsBom/bomIsDirty'] || false
      const illustrationsIsDirty =
        rootGetters['pdfImportPageDetailsIllustrations/illustrationsIsDirty'] || false
      return state.isDirty || bomIsDirty || illustrationsIsDirty
    },
    isLocked: (state) => state.locked,
    loading: (state) => state.loading,
    navigation: (state) => state.pageInfo?.navigation ?? null,
    originalPageFilename: (state) => state.originalPageFilename,
    pageFilename: (state) => state.pageInfo?.pageFilename ?? null,
    pageId: (state) => state.pageId,
    pageInfo: (state) => state.pageInfo,
    pageNumber: (state) => state.pageInfo?.pageNumber ?? null,
    pageOrigin: (state) => state.pageInfo?.pageOrigin ?? null,pageTitle: (state) => {
      if (state.pageInfo?.pageNumber == null) return ''
      const base = `${i18n.global.tc('page', 1)} ${state.pageInfo.pageNumber}`
      return state.pageInfo.copyNumber == null
        ? base
        : `${base} - ${i18n.global.t('pdfImportPageDetails.copySuffix')} ${state.pageInfo.copyNumber}`
    },
    positionInList: (state) => state.pageInfo?.positionInList ?? null,
    reviewStatus: (state) => state.pageInfo?.reviewStatus ?? null,
    saving: (state) => state.saving,
    sourceFilename: (state) => state.pageInfo?.sourceFilename ?? '',
    searchingMatchingPage: (state) => state.searchingMatchingPage,
    translation: (state) => state.pageInfo?.translation ?? null,
    totalCandidateCount: (state) => state.pageInfo?.totalCandidateCount ?? null
  },

  mutations: {
    RESET_STATE(state) {
      const nextMatchingPageLookupVersion = state.matchingPageLookupVersion + 1
      Object.assign(state, JSON.parse(JSON.stringify(initialState)))
      state.matchingPageLookupVersion = nextMatchingPageLookupVersion
    },

    REVERT_CHANGES(state) {
      if (state.originalPageInfo) {
        state.pageInfo = JSON.parse(JSON.stringify(state.originalPageInfo))
        state.isDirty = false
        state.matchingPageLookupVersion++
      }
    },

    SET_IMPORT_ID(state, importId: number) {
      state.importId = importId
    },

    SET_LOADING(state, loading: boolean) {
      state.loading = loading
    },

    SET_LOCKED(state, locked: boolean) {
      state.locked = locked
    },

    SET_PAGE_ID(state, pageId: number) {
      state.pageId = pageId
    },

    SET_PAGE_INFO(state, pageInfo: PageDetailsInfoResponse) {
      state.pageInfo = pageInfo
      state.locked = pageInfo.locked
      state.originalPageInfo = JSON.parse(JSON.stringify(pageInfo))
      state.originalPageFilename = state.originalPageInfo?.pageFilename ?? null
      state.isDirty = false
      state.matchingPageLookupVersion++
    },

    SET_SAVING(state, saving: boolean) {
      state.saving = saving
    },

    SET_SEARCHING_MATCHING_PAGE(state, searching: boolean) {
      state.searchingMatchingPage = searching
    },

    UPDATE_BOM_ITEM_COUNT(state, count: number) {
      if (state.pageInfo) {
        state.pageInfo.bomItemCount = count
      }
      if (state.originalPageInfo) {
        state.originalPageInfo.bomItemCount = count
      }
    },

    UPDATE_ILLUSTRATION_COUNT(state, count: number) {
      if (state.pageInfo) {
        state.pageInfo.illustrationCount = count
      }
      if (state.originalPageInfo) {
        state.originalPageInfo.illustrationCount = count
      }
    },

    UPDATE_MATCHING_PAGE(state, matchingPage: MatchingPageResult) {
      if (state.pageInfo) {
        state.pageInfo.matchingPage = matchingPage
      }
    },

    UPDATE_PAGE_FILENAME(state, filename: string | null) {
      if (state.pageInfo) {
        state.pageInfo.pageFilename = filename
        state.isDirty = true
        state.matchingPageLookupVersion++
      }
    },

    UPDATE_REVIEW_STATUS(state, status: ReviewStatus | null) {
      if (state.pageInfo) {
        state.pageInfo.reviewStatus = status
        state.isDirty = true
      }
    },

    UPDATE_TRANSLATION_DESCRIPTION(state, description: string | null) {
      if (state.pageInfo) {
        state.pageInfo.translation.description = description
        state.isDirty = true
      }
    },

    UPDATE_TRANSLATION_NAME(state, name: string | null) {
      if (state.pageInfo) {
        state.pageInfo.translation.name = name
        state.isDirty = true
        state.matchingPageLookupVersion++
      }
    },

    UPDATE_NAVIGATION(state, navigation: PageNavigation) {
      if (state.pageInfo) {
        state.pageInfo.navigation = navigation
      }
    }
  },

  actions: {
    async initialize({ commit, dispatch }, { importId, pageId, includeDropped = false }: { importId: number; pageId: number; includeDropped?: boolean }) {
      commit('SET_IMPORT_ID', importId)
      commit('SET_PAGE_ID', pageId)
      await dispatch('loadPageInfo', includeDropped)
    },

    async loadPageInfo({ state, commit }, includeDropped = false) {
      if (!state.importId || !state.pageId) return

      try {
        commit('SET_LOADING', true)
        const response = await PdfImportController.getPageInfo(state.importId, state.pageId, includeDropped)
        commit('SET_PAGE_INFO', response)
      } catch (error: any) {
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async duplicatePage({ state, commit }): Promise<number> {
      if (!state.importId || !state.pageId) throw new Error('No import/page ID')

      try {
        const response = await PdfImportController.duplicatePage(state.importId, state.pageId)

        // Overview isn't reloaded from here (it's not mounted); mark it stale so it
        // refreshes — including the new candidate — when the user navigates back.
        commit('pdfImportOverview/MARK_PAGES_STALE', null, { root: true })

        postSuccess({
          title: i18n.global.t('success'),
          text: i18n.global.t('pdfImportPageDetails.duplicateSuccessToast')
        })

        return response.pageId
      } catch (error: any) {
        const isLocked = error?.response?.status === 423
        postError({
          title: i18n.global.t('error'),
          text: isLocked
            ? i18n.global.t('pdfImport.lockedImportMessage')
            : i18n.global.t('pdfImportPageDetails.duplicateFailed')
        })
        throw error
      }
    },

    async publishPage({ state }, { skipAutoHotpointing }: { skipAutoHotpointing: boolean }) {
      if (!state.importId || !state.pageId) return

      try {
        await PdfImportController.publishSelected(state.importId, {
          pageIds: [state.pageId],
          bookTarget: null,
          skipAutoHotpointing
        })
        await router.push({ name: 'PdfImportsManager' })
      } catch (error: any) {
        const message =
          error?.response?.status === 423
            ? i18n.global.t('pdfImport.lockedImportMessage')
            : i18n.global.t('pdfImportPageDetails.notificationPublishFailed')
        postError({ title: i18n.global.t('error'), text: message })
      }
    },

    async refreshNavigation({ state, commit }, includeDropped = false) {
      if (!state.importId || !state.pageId) return

      try {
        // Fetch page info but only update navigation - no loading state for seamless UX
        const response = await PdfImportController.getPageInfo(state.importId, state.pageId, includeDropped)
        commit('UPDATE_NAVIGATION', response.navigation)
      } catch (error: any) {
        // Silently fail - navigation will remain in previous state
        console.error('Failed to refresh navigation:', error)
      }
    },

    async resetPage({ state, commit, dispatch }) {
      if (!state.importId || !state.pageId) return

      // Drop any not-yet-fired matching-page search so it doesn't run at all post-reset.
      // (An already in-flight call is instead caught by the matchingPageLookupVersion guard below.)
      debouncedSearchMatchingPage.cancel()

      let response: BulkResetPagesResponse

      try {
        response = await PdfImportController.resetSinglePage(state.importId, state.pageId)
      } catch (error: any) {
        const isLocked = error?.response?.status === 423
        postError({
          title: i18n.global.t('error'),
          text: isLocked
            ? i18n.global.t('pdfImport.lockedImportMessage')
            : (error?.response?.data?.message ?? i18n.global.t('pdfImportPageDetails.resetErrorToast'))
        })
        dispatch('searchMatchingPage')
        throw error
      }

      if (response.failedPageIds.includes(state.pageId)) {
        postWarning({
          title: i18n.global.t('warning'),
          text: i18n.global.t('pdfImportPageDetails.resetWarningToast')
        })
        dispatch('searchMatchingPage')
        return
      }

      // Reload parent page info (refreshes name, counts, status, origin; clears isDirty)
      await dispatch('loadPageInfo')

      // Reload child stores so BOM and Illustrations views reflect restored data
      const { importId, pageId } = state
      await Promise.all([
        dispatch('pdfImportPageDetailsBom/initialize', { importId, pageId }, { root: true }),
        dispatch('pdfImportPageDetailsIllustrations/initialize', { importId, pageId }, { root: true })
      ])

      // Mark Overview pages stale so page data reloads when user navigates back.
      // Filters are preserved — the page will be absent if it no longer matches them.
      commit('pdfImportOverview/MARK_PAGES_STALE', null, { root: true })

      postSuccess({
        title: i18n.global.t('success'),
        text: i18n.global.t('pdfImportPageDetails.resetSuccessToast')
      })
    },

    revertChanges({ commit }) {
      debouncedSearchMatchingPage.cancel()
      commit('REVERT_CHANGES')
    },

    teardown({ commit }) {
      debouncedSearchMatchingPage.cancel()
      commit('RESET_STATE')
    },

    async savePageInfo({ state, commit, dispatch, rootState }, { silent = false } = {}) {
      if (!state.importId || !state.pageId || !state.pageInfo) return

      try {
        commit('SET_SAVING', true)

        const request: PageDetailsInfoRequest = {
          pageFilename: state.pageInfo.pageFilename?.trim() ?? null,
          translationName: state.pageInfo.translation.name?.trim() ?? null,
          translationDescription: state.pageInfo.translation.description,
          reviewStatus: state.pageInfo.reviewStatus
        }

        const previousStatus = state.originalPageInfo?.reviewStatus ?? null
        const response = await PdfImportController.updatePageInfo(state.importId, state.pageId, request)
        commit('SET_PAGE_INFO', response)

        if (previousStatus !== response.reviewStatus) {
          commit('pdfImportOverview/ADJUST_SERVER_REVIEW_PROGRESS', {
            oldStatus: previousStatus,
            newStatus: response.reviewStatus
          }, { root: true })
        }

        // Sync non-status fields
        commit('pdfImportOverview/UPDATE_PAGE_DATA', {
          pageId: state.pageId,
          updates: {
            filename: response.pageFilename,
            pageTitle: response.translation.name
          }
        }, { root: true })

        // Sync review status via filter-aware mutation (removes tile if status no longer matches active filter)
        // Defensive: normalize undefined to null before passing to mutation
        const reviewStatusToSync = response.reviewStatus === undefined ? null : response.reviewStatus
        commit('pdfImportOverview/UPDATE_PAGE_REVIEW_STATUS', {
          pageIds: [state.pageId],
          reviewStatus: reviewStatusToSync
        }, { root: true })

        // If page was absent from overview (filtered out), check whether the new status now
        // matches the active filter — if so, mark stale so it reloads fresh when user returns.
        const overviewPages: PdfImportPageTile[] = rootState.pdfImportOverview.pages
        const isAbsentFromOverview = !overviewPages.some(p => p.pageId === state.pageId)
        if (isAbsentFromOverview) {
          const reviewFilter: ReviewStatus[] = rootState.pdfImportOverview.filters.reviewStatus
          const newStatus = response.reviewStatus
          const nowMatchesFilter =
            reviewFilter.length === 0 ||
            reviewFilter.includes(newStatus === null ? 'NO_STATUS' : newStatus)
          if (nowMatchesFilter) {
            commit('pdfImportOverview/MARK_STALE', null, { root: true })
          }
        }

        if (!silent) {
          postSuccess({
            title: i18n.global.t('success'),
            text: i18n.global.t('pdfImportPageDetails.notificationSaveSuccess')
          })
        }
      } catch (error: any) {
        const isLocked = error?.response?.status === 423
        postError({
          title: i18n.global.t('error'),
          text: isLocked
            ? i18n.global.t('pdfImport.lockedImportMessage')
            : i18n.global.t('pdfImportPageDetails.notificationSaveFailed')
        })
        throw error
      } finally {
        commit('SET_SAVING', false)
      }
    },

    searchMatchingPage(context: any) {
      return debouncedSearchMatchingPage(context)
    },

    updatePageFilename({ commit, dispatch }, filename: string | null) {
      commit('UPDATE_PAGE_FILENAME', filename)
      dispatch('searchMatchingPage')
    },

    updateReviewStatus({ commit }, status: ReviewStatus | null) {
      commit('UPDATE_REVIEW_STATUS', status)
    },

    updateTranslationDescription({ commit }, description: string | null) {
      commit('UPDATE_TRANSLATION_DESCRIPTION', description)
    },

    updateTranslationName({ commit, dispatch }, name: string | null) {
      commit('UPDATE_TRANSLATION_NAME', name)
      dispatch('searchMatchingPage')
    }
  }
}

export default pdfImportPageDetails
